import { useQuery, type UseQueryResult } from '@tanstack/react-query';

import { useApi } from '@/lib/api-context';

import { buildPermissionMatrix, type PermissionMatrix } from './matrix-presentation';
import { permissionsResponseSchema, rolesResponseSchema } from './types';

/**
 * Matriz rol×capacidad: combina el catálogo canónico de capacidades
 * (`/admin/permissions`) con los roles reales y sus permisos (`/admin/roles`).
 * Ambos son globales (no dependen de la empresa activa). Valida con Zod: un drift
 * cae al estado de error de la query, no revienta en render.
 */
/** `enabled` evita disparar los requests (que darían 403) cuando el actor no tiene
 *  el permiso o `/me` aún no cargó (fail-closed, sin ruido de red). */
export function useRolesMatrix({
  enabled,
}: {
  enabled: boolean;
}): UseQueryResult<PermissionMatrix> {
  const api = useApi();
  return useQuery({
    queryKey: ['roles-matrix'],
    queryFn: async () => {
      const [rolesRaw, permsRaw] = await Promise.all([
        api.get<unknown>('/api/v1/admin/roles'),
        api.get<unknown>('/api/v1/admin/permissions'),
      ]);
      const roles = rolesResponseSchema.parse(rolesRaw).data;
      const catalog = permissionsResponseSchema.parse(permsRaw).data;
      return buildPermissionMatrix(roles, catalog);
    },
    enabled,
  });
}
